import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PiggyBank, Package, TrendingDown, AlertTriangle,
  Calendar, ChevronDown, ChevronUp,
  ArrowRight, AlertCircle, Zap, X, Trophy, Sparkles,
} from 'lucide-react';
import { valueSummaryAPI } from '@/lib/api';
import useAnimatedValue from '@/lib/hooks/useAnimatedValue';

const fmt = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const pct = (v, t) => (t > 0 ? Math.round((v / t) * 100) : 0);

// ── Confetti ──────────────────────────────────────────────────────────────────
function ConfettiCanvas({ active }) {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);
  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    const COLORS = ['#d97706','#f59e0b','#fbbf24','#fde68a','#22c55e','#4ade80','#60a5fa','#f87171','#fff','#c4b5fd'];
    const pieces = Array.from({ length: 120 }, () => ({
      x: Math.random() * canvas.width, y: Math.random() * canvas.height * -1,
      w: 5 + Math.random() * 8, h: 9 + Math.random() * 6,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      rot: Math.random() * Math.PI * 2, rotSpd: (Math.random() - 0.5) * 0.1,
      vx: (Math.random() - 0.5) * 2, vy: 2.2 + Math.random() * 3, opacity: 1,
    }));
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = false;
      for (const p of pieces) {
        p.x += p.vx; p.y += p.vy; p.rot += p.rotSpd;
        if (p.y > canvas.height * 0.72) p.opacity = Math.max(0, p.opacity - 0.018);
        if (p.opacity <= 0) continue;
        alive = true;
        ctx.save(); ctx.globalAlpha = p.opacity;
        ctx.translate(p.x, p.y); ctx.rotate(p.rot);
        ctx.fillStyle = p.color; ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      if (alive) animRef.current = requestAnimationFrame(draw);
    };
    animRef.current = requestAnimationFrame(draw);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [active]);
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />;
}

// ── Realized popup ────────────────────────────────────────────────────────────
function RealizedPopup({ data, onClose }) {
  const rs        = data?.realized_savings || {};
  const animTotal = useAnimatedValue(rs.total || 0);
  const count     = rs.actions_count || 0;

  const rows = [
    { label: 'Dead Stock cleared',    value: rs.dead_stock,     color: 'text-amber-600',  bg: 'bg-amber-50',  border: 'border-amber-200'  },
    { label: 'Reorder actions taken', value: rs.reorder,        color: 'text-blue-600',   bg: 'bg-blue-50',   border: 'border-blue-200'   },
    { label: 'Expiry risk avoided',   value: rs.expiry,         color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200' },
    { label: 'Margin leakage fixed',  value: rs.margin_leakage, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
  ].filter(r => r.value > 0);

  const headline = count >= 10 ? 'Outstanding work!' : count >= 5 ? "You're on a roll!" : 'Great start!';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backdropFilter: 'blur(12px)', backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl"
        style={{
          background: 'linear-gradient(160deg, #fffbeb 0%, #fef3c7 55%, #fffde7 100%)',
          border: '2px solid #d97706',
          boxShadow: '0 25px 60px rgba(217,119,6,0.25)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <ConfettiCanvas active />
        <button
          onClick={e => { e.stopPropagation(); onClose(); }}
          className="absolute top-4 right-4 z-20 p-1.5 rounded-full bg-amber-100/80 hover:bg-amber-200 text-amber-700 transition-colors duration-150"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <div className="relative z-10 px-8 py-8 flex flex-col items-center text-center">
          <div className="mb-4 p-4 rounded-2xl bg-gradient-to-br from-amber-100 to-amber-200 shadow-inner border border-amber-300">
            <Trophy className="h-10 w-10 text-amber-600" />
          </div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-amber-500 mb-0.5">Realized to Date</p>
          <h2 className="text-2xl font-bold text-amber-900 mb-0.5">Congratulations!</h2>
          <p className="text-sm text-amber-700/70 mb-5">{headline}</p>

          <div
            className="w-full rounded-2xl px-6 py-5 mb-4 text-center"
            style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid #fcd34d', boxShadow: 'inset 0 2px 8px rgba(217,119,6,0.08)' }}
          >
            <p className="text-5xl font-bold text-amber-700 tracking-tight">{fmt(animTotal)}</p>
            {count > 0 && (
              <p className="text-xs text-amber-600/60 mt-2">
                across <span className="font-semibold text-amber-700">{count}</span> action{count !== 1 ? 's' : ''} addressed
              </p>
            )}
          </div>

          {rows.length > 0 && (
            <div className="w-full space-y-1.5 mb-5">
              {rows.map(r => (
                <div key={r.label} className={`flex items-center justify-between rounded-xl px-4 py-2.5 border ${r.bg} ${r.border}`}>
                  <span className="text-xs text-foreground/70">{r.label}</span>
                  <span className={`font-bold text-sm ${r.color}`}>{fmt(r.value)}</span>
                </div>
              ))}
            </div>
          )}

          <Link
            to="/smart-actions" onClick={onClose}
            className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors duration-150 text-sm shadow-lg shadow-amber-200"
          >
            <Zap className="h-4 w-4" /> Address more actions
          </Link>
          <p className="text-[11px] text-amber-500/60 mt-3">Every action grows this number</p>
        </div>
      </div>
    </div>
  );
}

// ── Channel card ─────────────────────────────────────────────────────────────
const CARD_META = {
  amber:  { top: 'border-t-amber-400',  icon: 'text-amber-500',  val: 'text-amber-700',  cta: 'text-amber-600',  hoverShadow: 'hover:shadow-amber-100'  },
  red:    { top: 'border-t-red-400',    icon: 'text-red-500',    val: 'text-red-700',    cta: 'text-red-600',    hoverShadow: 'hover:shadow-red-100'    },
  orange: { top: 'border-t-orange-400', icon: 'text-orange-500', val: 'text-orange-700', cta: 'text-orange-600', hoverShadow: 'hover:shadow-orange-100' },
  purple: { top: 'border-t-purple-400', icon: 'text-purple-500', val: 'text-purple-700', cta: 'text-purple-600', hoverShadow: 'hover:shadow-purple-100' },
};

function ChannelCard({ icon: Icon, title, value, subMetric, color, explanation, cta, ctaLabel, unavailable, share }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const c = CARD_META[color] || CARD_META.amber;

  return (
    <div
      onClick={() => cta && navigate(cta)}
      className={`group bg-card rounded-2xl border border-border border-t-4 ${c.top} p-5 flex flex-col gap-3 shadow-sm hover:-translate-y-1 hover:shadow-lg ${c.hoverShadow} transition-all duration-200 ${cta ? 'cursor-pointer' : 'cursor-default'}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${c.icon} transition-transform duration-200 group-hover:scale-110`} />
          <span className="font-semibold text-sm text-foreground">{title}</span>
        </div>
        {value > 0 && share != null && (
          <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-400 group-hover:bg-slate-200 transition-colors duration-150">
            {share > 0 ? `${share}%` : '<1%'}
          </span>
        )}
      </div>

      {unavailable ? (
        <div className="flex-1 flex flex-col gap-2">
          <p className="text-sm text-muted-foreground italic leading-relaxed">{unavailable}</p>
          {cta && (
            <Link to={cta} onClick={e => e.stopPropagation()} className={`inline-flex items-center gap-1 text-xs font-medium ${c.cta} hover:underline group/cta`}>
              {ctaLabel} <ArrowRight className="h-3 w-3 transition-transform duration-150 group-hover/cta:translate-x-0.5" />
            </Link>
          )}
          {explanation && (
            <>
              <button
                onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
                className="flex items-center gap-1 text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors duration-150 w-fit mt-auto"
              >
                {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                How we calculated this
              </button>
              {open && (
                <p className="text-xs text-muted-foreground leading-relaxed border-t border-slate-100 pt-2">
                  {explanation}
                </p>
              )}
            </>
          )}
        </div>
      ) : (
        <>
          <div className="flex-1">
            <p className={`text-2xl font-bold ${c.val}`}>{fmt(value)}</p>
            {subMetric && <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{subMetric}</p>}
          </div>
          {cta && (
            <Link to={cta} onClick={e => e.stopPropagation()} className={`group/cta inline-flex items-center gap-1 text-xs font-medium ${c.cta} hover:underline w-fit`}>
              {ctaLabel} <ArrowRight className="h-3 w-3 transition-transform duration-150 group-hover/cta:translate-x-0.5" />
            </Link>
          )}
          <button
            onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
            className="flex items-center gap-1 text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors duration-150 w-fit mt-auto"
          >
            {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            How we calculated this
          </button>
          {open && (
            <p className="text-xs text-muted-foreground leading-relaxed border-t border-slate-100 pt-2">
              {explanation}
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <p className="text-sm text-muted-foreground text-center py-2">
        Calculating your savings across 90 days of data…
      </p>
      <div className="flex items-center justify-between gap-6">
        <div className="h-12 bg-muted rounded-xl w-52" />
        <div className="h-16 bg-muted rounded-2xl w-72" />
      </div>
      <div className="h-36 bg-muted rounded-2xl" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <div key={i} className="h-44 bg-muted rounded-2xl" />)}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
const YourSavings = () => {
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [showRealized, setShowRealized] = useState(false);

  useEffect(() => {
    valueSummaryAPI.get()
      .then(res => setData(res.data))
      .catch(() => setError('Could not load savings data. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const animatedTotal    = useAnimatedValue(data?.total_value_identified || 0);
  const animatedRealized = useAnimatedValue(data?.realized_savings?.total || 0);

  if (loading) return <div className="p-6 lg:p-8"><Skeleton /></div>;
  if (error) return (
    <div className="p-6 lg:p-8 flex items-center gap-3 text-destructive">
      <AlertCircle className="h-5 w-5" /><span className="text-sm">{error}</span>
    </div>
  );
  if (data?.no_data) return (
    <div className="p-6 lg:p-8 text-center py-20">
      <div className="p-3 bg-emerald-100 rounded-2xl w-fit mx-auto mb-4">
        <PiggyBank className="h-10 w-10 text-emerald-500 opacity-50" />
      </div>
      <p className="font-semibold text-foreground/60 text-lg mb-1">Store not initialized yet</p>
      <p className="text-sm text-muted-foreground max-w-xs mx-auto">
        Your administrator needs to complete the opening stock setup before savings tracking becomes available.
      </p>
    </div>
  );

  const d  = data;
  const ds = d.dead_stock;
  const rv = d.revenue_at_risk;
  const ml = d.margin_leakage;
  const ex = d.expiry_risk;
  const sm = d.slow_movers;
  const deadStockDays   = d.dead_stock_days ?? 30;
  const expiryAlertDays = d.expiry_alert_days ?? 15;

  const noExpiryData  = ex.products_with_expiry === 0;
  const partialExpiry = ex.products_with_expiry > 0 && ex.products_with_expiry < ex.total_products;
  const isEmpty       = d.total_value_identified === 0;
  const hasRealized   = (d?.realized_savings?.total || 0) > 0;
  const total         = d.total_value_identified;

  const BAR_SEGMENTS = [
    { value: ds.value,                    color: '#fcd34d', label: 'Dead Stock'      },
    { value: rv.value,                    color: '#fca5a5', label: 'Revenue at Risk' },
    { value: ml.value,                    color: '#fdba74', label: 'Margin Leakage'  },
    { value: noExpiryData ? 0 : ex.value, color: '#d8b4fe', label: 'Expiry Risk'     },
  ].filter(s => s.value > 0);

  return (
    <div className="p-6 lg:p-8 space-y-6">

      {/* ── Title row + Realized tile (horizontal, rectangular) ─────────── */}
      <div className="flex items-center justify-between gap-6">

        {/* Left: page title */}
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-xl">
            <PiggyBank className="h-6 w-6 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Your Savings</h1>
            <p className="text-muted-foreground text-xs mt-0.5">How KiranaFlow is creating value for your store</p>
          </div>
        </div>

        {/* Right: Realized to Date — horizontal golden tile */}
        <button
          onClick={() => hasRealized && setShowRealized(true)}
          disabled={!hasRealized}
          className={`group flex items-center gap-4 px-5 py-3.5 rounded-2xl border-2 transition-all duration-200 ${
            hasRealized
              ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-xl'
              : 'cursor-default opacity-55'
          }`}
          style={hasRealized ? {
            background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 60%, #fef9c3 100%)',
            borderColor: '#fcd34d',
            boxShadow: '0 2px 12px rgba(251,191,36,0.2)',
          } : {
            background: '#f8fafc',
            borderColor: '#e2e8f0',
          }}
        >
          {/* Trophy badge */}
          <div className={`p-2.5 rounded-xl transition-transform duration-200 group-hover:scale-105 ${hasRealized ? 'bg-amber-100 border border-amber-300' : 'bg-slate-100'}`}>
            <Trophy className={`h-5 w-5 ${hasRealized ? 'text-amber-600' : 'text-slate-400'}`} />
          </div>

          {/* Text */}
          <div className="text-left">
            <p className={`text-[11px] font-bold uppercase tracking-widest ${hasRealized ? 'text-amber-600' : 'text-muted-foreground'}`}>
              Realized to Date
            </p>
            <p className={`text-2xl font-bold leading-tight ${hasRealized ? 'text-amber-800' : 'text-muted-foreground'}`}>
              {fmt(animatedRealized)}
            </p>
            <p className={`text-[11px] mt-0.5 ${hasRealized ? 'text-amber-600/70' : 'text-muted-foreground/50'}`}>
              {hasRealized
                ? `${d.realized_savings.actions_count} action${d.realized_savings.actions_count !== 1 ? 's' : ''} addressed`
                : 'Address actions to grow this'}
            </p>
          </div>

          {/* Hover sparkle */}
          {hasRealized && (
            <Sparkles className="h-4 w-4 text-amber-400 ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
          )}
        </button>
      </div>

      {/* ── Identified Today hero ───────────────────────────────────────── */}
      {!isEmpty && (
        <div className="rounded-2xl bg-gradient-to-br from-emerald-50 via-white to-emerald-50/30 border border-emerald-200 p-6 shadow-sm hover:shadow-md hover:border-emerald-300 transition-all duration-200">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-lg bg-emerald-100">
              <Zap className="h-3.5 w-3.5 text-emerald-600" />
            </div>
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">Identified Today</span>
          </div>

          <p className="text-5xl font-bold text-emerald-700 tracking-tight mb-1">{fmt(animatedTotal)}</p>
          <p className="text-xs text-emerald-700/50 mb-4">Total savings opportunity across your store right now</p>

          {/* Stacked proportion bar */}
          {BAR_SEGMENTS.length > 0 && (
            <div>
              <p className="text-[11px] text-muted-foreground/50 uppercase tracking-wide font-medium mb-1.5">
                Breakdown — 4 channels below
              </p>
              <div className="flex h-2.5 rounded-full overflow-hidden gap-px bg-slate-100">
                {BAR_SEGMENTS.map(s => (
                  <div
                    key={s.label}
                    style={{ width: `${(s.value / total) * 100}%`, backgroundColor: s.color }}
                    title={`${s.label}: ${fmt(s.value)} (${pct(s.value, total)}%)`}
                    className="transition-all duration-300"
                  />
                ))}
              </div>
              <div className="flex flex-wrap gap-3 mt-2">
                {BAR_SEGMENTS.map(s => (
                  <span key={s.label} className="flex items-center gap-1.5 text-[11px] text-muted-foreground/70">
                    <span className="inline-block h-2 w-2 rounded-sm" style={{ backgroundColor: s.color }} />
                    {s.label} · {pct(s.value, total)}%
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {isEmpty && (
        <div className="text-center py-12 text-muted-foreground">
          <PiggyBank className="h-12 w-12 mx-auto mb-3 opacity-20" />
          <p className="font-medium text-foreground/60">No savings identified yet</p>
          <p className="text-sm mt-1">Add sales, purchase, and stock data — opportunities will surface here.</p>
        </div>
      )}

      {/* ── 4 channel cards ─────────────────────────────────────────────── */}
      {!isEmpty && (
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/50 mb-3">Savings Breakdown</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

            <ChannelCard
              icon={Package}        title="Dead Stock"       color="amber"
              value={ds.value}      share={pct(ds.value, total)}
              subMetric={ds.item_count > 0 ? `${ds.item_count} product${ds.item_count !== 1 ? 's' : ''} idle ${deadStockDays}+ days` : 'No idle stock — great!'}
              explanation={`Products with stock unsold for ${deadStockDays}+ days. Unsold units × cost price.`}
              cta="/stock?tab=dead"  ctaLabel="View dead stock"
            />

            <ChannelCard
              icon={TrendingDown}   title="Revenue at Risk"  color="red"
              value={rv.value}      share={pct(rv.value, total)}
              subMetric={
                rv.product_count > 0
                  ? [
                      rv.oos_count > 0 && `${rv.oos_count} out of stock`,
                      rv.low_stock_count > 0 && `${rv.low_stock_count} running low`,
                    ].filter(Boolean).join(', ')
                  : 'All stock levels healthy'
              }
              explanation="Out-of-stock and critically low-stock products. Revenue lost if not restocked within your lead time."
              cta="/stock?tab=low"  ctaLabel="Check Stock Levels"
            />

            <ChannelCard
              icon={AlertTriangle}  title="Margin Leakage"   color="orange"
              value={ml.value}      share={pct(ml.value, total)}
              subMetric={
                ml.product_count > 0
                  ? `${ml.product_count} product${ml.product_count !== 1 ? 's' : ''} projected to lose margin in next 30 days`
                  : 'No margin leakage projected'
              }
              explanation="Products selling below cost. Projected loss = loss per unit × daily units × 30 days."
              cta="/stock?tab=leakage" ctaLabel="Fix Pricing"
            />

            <ChannelCard
              icon={Calendar}       title="Expiry Risk"      color="purple"
              value={noExpiryData ? 0 : ex.value}
              share={pct(noExpiryData ? 0 : ex.value, total)}
              subMetric={
                noExpiryData ? undefined
                : ex.loss_count > 0 && ex.risk_count > 0 ? `${ex.loss_count} expired · ${ex.risk_count} expiring soon`
                : ex.loss_count > 0 ? `${ex.loss_count} product${ex.loss_count !== 1 ? 's' : ''} already expired`
                : ex.risk_count > 0 ? `${ex.risk_count} product${ex.risk_count !== 1 ? 's' : ''} expiring in ${expiryAlertDays} days`
                : `No expiry risk in ${expiryAlertDays} days`
              }
              explanation={`Expired stock is a direct write-off. Stock expiring within ${expiryAlertDays} days is urgent. At-risk units × cost price.`}
              cta="/stock?tab=expiry" ctaLabel="Check expiry items"
              unavailable={noExpiryData ? 'No batch/expiry data found. Upload opening stock with both batch number and expiry date to track this channel.' : null}
            />

          </div>
        </div>
      )}

      {/* ── Notices ─────────────────────────────────────────────────────── */}
      {partialExpiry && !isEmpty && (
        <div className="flex items-start gap-3 bg-purple-50 border border-purple-200 rounded-xl px-4 py-3">
          <AlertCircle className="h-4 w-4 text-purple-600 shrink-0 mt-0.5" />
          <p className="text-sm text-purple-800">
            Only <strong>{ex.products_with_expiry}</strong> of <strong>{ex.total_products}</strong> products have expiry dates.{' '}
            <Link to="/stock" className="underline font-medium">Add them in Stock</Link> for better coverage.
          </p>
        </div>
      )}

      {sm.item_count > 0 && (
        <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 hover:border-emerald-300 transition-colors duration-150">
          <TrendingDown className="h-4 w-4 text-emerald-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-emerald-800">
              {sm.item_count} product{sm.item_count !== 1 ? 's are' : ' is'} losing momentum
            </p>
            <p className="text-xs text-emerald-700/70 mt-0.5">
              Sales dropped 50%+ vs. the previous 30 days.{' '}
              {sm.value_at_risk > 0 && <span className="font-medium">{fmt(sm.value_at_risk)} at risk of becoming dead stock.</span>}
            </p>
            <div className="flex flex-wrap gap-1 mt-1.5">
              {sm.top_items.slice(0, 3).map((item, i) => (
                <span key={i} className="text-xs bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">{item.name} ({item.last30} → {item.prev30})</span>
              ))}
            </div>
          </div>
          <Link to="/stock" className="text-xs font-medium text-emerald-700 hover:underline shrink-0 group/sl flex items-center gap-0.5">
            View Stock <ArrowRight className="h-3 w-3 transition-transform duration-150 group-hover/sl:translate-x-0.5" />
          </Link>
        </div>
      )}

      {showRealized && <RealizedPopup data={data} onClose={() => setShowRealized(false)} />}
    </div>
  );
};

export default YourSavings;
